import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContactsTable from './components/ContactsTable';
import ContactFilters from './components/ContactFilters';
import ContactDrawer from './components/ContactDrawer';
import BulkActions from './components/BulkActions';
import ContactsPagination from './components/ContactsPagination';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import CSVImportModal from './components/CSVImportModal';

const CLIENT_STATUSES = ['Lead', 'Active', 'Returning', 'VIP', 'Inactive', 'Blocked'];
const LEAD_SOURCES = ['Website', 'Referral', 'Google', 'Facebook', 'Thumbtack', 'Yelp', 'HomeAdvisor', 'Angi', 'Nextdoor', 'Word of Mouth', 'Other'];
const PAYMENT_METHODS = ['Cash', 'Check', 'Credit Card', 'Debit Card', 'Zelle', 'Venmo', 'PayPal', 'ACH', 'Other'];

const STATUS_META = {
  Lead:      { color: 'bg-blue-500/10 text-blue-600 border-blue-200 dark:border-blue-800', dot: 'bg-blue-500' },
  Active:    { color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200 dark:border-emerald-800', dot: 'bg-emerald-500' },
  Returning: { color: 'bg-violet-500/10 text-violet-600 border-violet-200 dark:border-violet-800', dot: 'bg-violet-500' },
  VIP:       { color: 'bg-amber-500/10 text-amber-600 border-amber-200 dark:border-amber-800', dot: 'bg-amber-500' },
  Inactive:  { color: 'bg-slate-400/10 text-slate-500 border-slate-200 dark:border-slate-700', dot: 'bg-slate-400' },
  Blocked:   { color: 'bg-red-500/10 text-red-600 border-red-200 dark:border-red-800', dot: 'bg-red-500' },
};

const ContactsPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [contacts, setContacts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ company: '', status: '', source: '' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '', last_name: '', email: '', phone: '', title: '',
    address: '', city: '', state: '', zip: '',
    lead_source: '', payment_method: '', notes: '', client_status: 'Lead'
  });
  const [saving, setSaving] = useState(false);
  const [isCSVImportOpen, setIsCSVImportOpen] = useState(false);
  const { profile } = useAuth();

  // Status counts for the header pills
  const statusCounts = CLIENT_STATUSES?.reduce((acc, s) => {
    acc[s] = contacts?.filter(c => (c?.client_status || 'Lead') === s)?.length;
    return acc;
  }, {});

  const fetchContacts = useCallback(async () => {
    if (!profile?.org_id) return;
    setLoading(true);
    setError('');
    try {
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      let query = supabase?.from('contacts')
        ?.select('*, accounts(name), owner:profiles(full_name)', { count: 'exact' })
        ?.eq('org_id', profile?.org_id)
        ?.is('deleted_at', null)
        ?.order('created_at', { ascending: false })
        ?.range(from, to);

      if (searchTerm) {
        query = query?.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }
      if (filters?.status) {
        query = query?.eq('client_status', filters?.status);
      }

      const { data, error: fetchError, count } = await query;
      if (fetchError) throw fetchError;
      setContacts(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [profile?.org_id, currentPage, itemsPerPage, searchTerm, filters]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedContact(null);
  };

  const handleSelectContact = (contactId, checked) => {
    if (checked) setSelectedContacts(prev => [...prev, contactId]);
    else setSelectedContacts(prev => prev?.filter(id => id !== contactId));
  };

  const handleSelectAllContacts = (checked) => {
    if (checked) setSelectedContacts(contacts?.map(c => c?.id));
    else setSelectedContacts([]);
  };

  const handleAddContact = async () => {
    if (!newContact?.first_name?.trim()) return;
    setSaving(true);
    try {
      const { error: insertError } = await supabase?.from('contacts')?.insert({
        ...newContact,
        org_id: profile?.org_id,
        owner_id: profile?.id
      });
      if (insertError) throw insertError;

      await supabase?.from('activities')?.insert({
        org_id: profile?.org_id,
        owner_id: profile?.id,
        type: 'note',
        subject: `Contact Added: ${newContact?.first_name} ${newContact?.last_name || ''}`?.trim(),
        description: `New contact created${newContact?.email ? ` — ${newContact?.email}` : ''}`,
        due_date: new Date()?.toISOString(),
        priority: 'low',
        status: 'completed'
      });

      setNewContact({
        first_name: '', last_name: '', email: '', phone: '', title: '',
        address: '', city: '', state: '', zip: '',
        lead_source: '', payment_method: '', notes: '', client_status: 'Lead'
      });
      setIsAddModalOpen(false);
      fetchContacts();
      toast?.success('Contact added successfully');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Move this contact to trash?')) return;
    try {
      await supabase?.from('contacts')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', contactId);
      toast?.success('Moved to Trash');
      fetchContacts();
      if (selectedContact?.id === contactId) handleDrawerClose();
    } catch (err) {
      setError(err?.message);
    }
  };

  const mappedContacts = contacts?.map(c => ({
    ...c,
    id: c?.id,
    name: `${c?.first_name} ${c?.last_name}`?.trim(),
    company: c?.accounts?.name || '',
    email: c?.email || '',
    phone: c?.phone || '',
    title: c?.title || '',
    client_status: c?.client_status || 'Lead',
    lastContact: c?.updated_at?.split('T')?.[0] || c?.created_at?.split('T')?.[0] || ''
  }));

  const activeFiltersCount = Object.values(filters)?.filter(v => v !== '')?.length;

  return (
    <>
      <Helmet>
        <title>Contacts - Wilcart CRM</title>
        <meta name="description" content="Manage your contacts in Wilcart CRM." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6">

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon name="Users" size={18} className="text-primary" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground tracking-tight">Contacts</h1>
                  <span className="px-2.5 py-0.5 bg-muted text-muted-foreground text-xs font-semibold rounded-full border border-border">
                    {totalCount?.toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground ml-12">Manage your customer relationships and lifecycle</p>
              </div>
              <div className="flex items-center gap-2 ml-12 sm:ml-0">
                <Button variant="outline" size="sm" onClick={() => setIsCSVImportOpen(true)}>
                  <Icon name="Upload" size={14} className="mr-1.5" />
                  Import
                </Button>
                <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
                  <Icon name="Plus" size={14} className="mr-1.5" />
                  Add Contact
                </Button>
              </div>
            </div>

            {/* Status Pills Row */}
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() => { setFilters(p => ({...p, status: ''})); setCurrentPage(1); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${!filters?.status ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground border-border hover:border-foreground/30'}`}
              >
                All
                <span className="font-bold">{totalCount}</span>
              </button>
              {CLIENT_STATUSES?.map(s => {
                const meta = STATUS_META?.[s];
                const isActive = filters?.status === s;
                return (
                  <button
                    key={s}
                    onClick={() => { setFilters(p => ({...p, status: isActive ? '' : s})); setCurrentPage(1); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${isActive ? meta?.color + ' font-semibold' : 'bg-card text-muted-foreground border-border hover:border-foreground/30'}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot}`} />
                    {s}
                    <span className="font-bold">{statusCounts?.[s] || 0}</span>
                  </button>
                );
              })}
            </div>

            {/* Search + Filters */}
            <div className="bg-card border border-border rounded-xl p-3 mb-4 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e?.target?.value); setCurrentPage(1); }}
                  className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
              </div>
              <ContactFilters
                filters={filters}
                onFilterChange={(key, value) => { setFilters(prev => ({...prev, [key]: value})); setCurrentPage(1); }}
                onClearFilters={() => { setFilters({ company: '', status: '', source: '' }); setCurrentPage(1); }}
                activeFiltersCount={activeFiltersCount}
                companies={[...new Set(contacts?.map(c => c?.accounts?.name)?.filter(Boolean))]}
                roles={[...new Set(contacts?.map(c => c?.title)?.filter(Boolean))]}
                inline
              />
            </div>

            {selectedContacts?.length > 0 && (
              <BulkActions
                selectedCount={selectedContacts?.length}
                onExport={() => console.log('Export', selectedContacts)}
                onEmail={() => console.log('Email', selectedContacts)}
                onBulkEmail={() => console.log('Email', selectedContacts)}
                onBulkTag={() => console.log('Tag', selectedContacts)}
                onDelete={async () => {
                  if (!window.confirm(`Move ${selectedContacts?.length} contact(s) to trash?`)) return;
                  try {
                    await Promise.all(selectedContacts?.map(id =>
                      supabase?.from('contacts')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', id)
                    ));
                    toast?.success(`Moved ${selectedContacts?.length} contact(s) to Trash`);
                    setSelectedContacts([]);
                    fetchContacts();
                  } catch (err) { setError(err?.message); }
                }}
                onBulkDelete={async () => {
                  if (!window.confirm(`Move ${selectedContacts?.length} contact(s) to trash?`)) return;
                  try {
                    await Promise.all(selectedContacts?.map(id =>
                      supabase?.from('contacts')?.update({ deleted_at: new Date()?.toISOString() })?.eq('id', id)
                    ));
                    toast?.success(`Moved ${selectedContacts?.length} contact(s) to Trash`);
                    setSelectedContacts([]);
                    fetchContacts();
                  } catch (err) { setError(err?.message); }
                }}
                onClear={() => setSelectedContacts([])}
              />
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">{error}</div>
            )}

            {loading ? (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border bg-muted/30">
                  <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                </div>
                <div className="divide-y divide-border">
                  {[1,2,3,4,5,6]?.map(i => (
                    <div key={i} className="flex items-center gap-4 px-4 py-4">
                      <div className="w-4 h-4 bg-muted animate-pulse rounded" />
                      <div className="w-9 h-9 bg-muted animate-pulse rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-36 bg-muted animate-pulse rounded" />
                        <div className="h-3 w-24 bg-muted animate-pulse rounded" />
                      </div>
                      <div className="h-3 w-40 bg-muted animate-pulse rounded hidden lg:block" />
                      <div className="h-3 w-28 bg-muted animate-pulse rounded hidden lg:block" />
                      <div className="h-5 w-16 bg-muted animate-pulse rounded-full" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <ContactsTable
                contacts={mappedContacts}
                selectedContacts={selectedContacts}
                onSelectContact={handleSelectContact}
                onSelectAll={handleSelectAllContacts}
                onSelectAllContacts={handleSelectAllContacts}
                onContactClick={handleContactClick}
                sortConfig={{ key: 'name', direction: 'asc' }}
                onSort={() => {}}
                onDelete={handleDeleteContact}
                statusMeta={STATUS_META}
              />
            )}

            <ContactsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={() => {}}
            />
          </div>
        </main>

        {/* Add Contact Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="UserPlus" size={16} className="text-primary" />
                  </div>
                  <h2 className="text-base font-semibold text-foreground">New Contact</h2>
                </div>
                <button onClick={() => setIsAddModalOpen(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                {/* Status */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Client Status</label>
                  <div className="flex flex-wrap gap-2">
                    {CLIENT_STATUSES?.map(s => {
                      const meta = STATUS_META?.[s];
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setNewContact(p => ({...p, client_status: s}))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${newContact?.client_status === s ? meta?.color + ' ring-2 ring-offset-1 ring-primary/30' : 'bg-muted/50 text-muted-foreground border-border hover:border-foreground/30'}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${meta?.dot}`} />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Name */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">First Name *</label>
                    <input type="text" value={newContact?.first_name} onChange={e => setNewContact(p => ({...p, first_name: e?.target?.value}))}
                      placeholder="John"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Last Name</label>
                    <input type="text" value={newContact?.last_name} onChange={e => setNewContact(p => ({...p, last_name: e?.target?.value}))}
                      placeholder="Smith"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                </div>
                {/* Contact */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Email</label>
                    <input type="email" value={newContact?.email} onChange={e => setNewContact(p => ({...p, email: e?.target?.value}))}
                      placeholder="john@example.com"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Phone</label>
                    <input type="tel" value={newContact?.phone} onChange={e => setNewContact(p => ({...p, phone: e?.target?.value}))}
                      placeholder="(555) 000-0000"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                </div>
                {/* Title */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Job Title</label>
                  <input type="text" value={newContact?.title} onChange={e => setNewContact(p => ({...p, title: e?.target?.value}))}
                    placeholder="Homeowner"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                </div>
                {/* Address */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Street Address</label>
                  <input type="text" value={newContact?.address} onChange={e => setNewContact(p => ({...p, address: e?.target?.value}))}
                    placeholder="123 Main St"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">City</label>
                    <input type="text" value={newContact?.city} onChange={e => setNewContact(p => ({...p, city: e?.target?.value}))}
                      placeholder="Austin"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">State</label>
                    <input type="text" value={newContact?.state} onChange={e => setNewContact(p => ({...p, state: e?.target?.value}))}
                      placeholder="TX"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">ZIP</label>
                    <input type="text" value={newContact?.zip} onChange={e => setNewContact(p => ({...p, zip: e?.target?.value}))}
                      placeholder="78701"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background transition-colors" />
                  </div>
                </div>
                {/* Lead Source */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Lead Source</label>
                  <select value={newContact?.lead_source} onChange={e => setNewContact(p => ({...p, lead_source: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background text-foreground transition-colors">
                    <option value="">Select source...</option>
                    {LEAD_SOURCES?.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {/* Payment Method */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Preferred Payment</label>
                  <select value={newContact?.payment_method} onChange={e => setNewContact(p => ({...p, payment_method: e?.target?.value}))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background text-foreground transition-colors">
                    <option value="">Select payment...</option>
                    {PAYMENT_METHODS?.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                {/* Notes */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Notes</label>
                  <textarea value={newContact?.notes} onChange={e => setNewContact(p => ({...p, notes: e?.target?.value}))}
                    placeholder="Any additional notes..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-background resize-none transition-colors" />
                </div>
              </div>
              <div className="flex gap-3 px-6 py-4 border-t border-border flex-shrink-0">
                <Button variant="outline" className="flex-1" onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
                <Button className="flex-1" onClick={handleAddContact} disabled={saving || !newContact?.first_name?.trim()}>
                  {saving ? 'Saving...' : 'Add Contact'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <ContactDrawer
          contact={selectedContact}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onDelete={handleDeleteContact}
          onUpdate={fetchContacts}
          statusMeta={STATUS_META}
          clientStatuses={CLIENT_STATUSES}
        />

        <CSVImportModal
          isOpen={isCSVImportOpen}
          onClose={() => setIsCSVImportOpen(false)}
          onImportComplete={() => { fetchContacts(); setIsCSVImportOpen(false); }}
        />
      </div>
    </>
  );
};

export default ContactsPage;